import React from 'react';
import { useDisclosure, VStack, Collapse, Box } from '@wegroup/design-system';
import { DraggableProvided } from 'react-beautiful-dnd';
import InsurancePolicyHeader from './header/InsurancePolicyHeader';
import InsurancePolicyRisksTable from './table/InsurancePolicyRisksTable';
import { InsurancePolicyItemProps } from './InsurancePolicyItem';
import InsurancePolicyClausesButton from './clauses/InsurancePolicyClausesButton';
import InsurancePolicyDocumentButton from './InsurancePolicyDocumentButton';
import { InsurancePolicyItemCardDocuments } from '@advisory-report/src/features/creation-flow/components/insurance-policies/shared/insurance-policy/InsurancePolicyItemCardDocuments';

interface Props {
  provided?: DraggableProvided;
  withDocuments?: boolean;
}

const InsurancePolicyItemCard: React.FC<Props & InsurancePolicyItemProps> = ({
  provided,
  insurancePolicy,
  partyType,
  partyId,
  customRiskObjects = [],
  withActions = true,
  defaultIsOpen = true,
  controlled,
  hideAIBanner,
  customActions,
  onEdit,
  onRemove,
  withDocuments,
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen });
  const showClauses = !!insurancePolicy.clauses?.length;
  const showPolicyDocument = !!insurancePolicy.policy_document_link;

  // extra filter to not have empty links, just in case
  const documentsWithLink =
    (insurancePolicy.documents &&
      insurancePolicy.documents.filter(
        (document) => 'link' in document && document.link,
      )) ||
    [];

  const showDocuments = withDocuments && !!documentsWithLink.length;
  const showButtonActions = showClauses || showPolicyDocument;

  return (
    <VStack
      mb="2"
      borderRadius="md"
      bg="white"
      border="1px"
      borderColor="gray.50"
      boxShadow="level1"
      ref={provided?.innerRef}
      align="stretch"
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
    >
      <InsurancePolicyHeader
        isOpen={isOpen}
        onToggle={onToggle}
        insurancePolicy={insurancePolicy}
        partyType={partyType}
        withActions={withActions}
        customRiskObjects={customRiskObjects}
        controlled={controlled}
        hideAIBanner={hideAIBanner}
        onEdit={onEdit}
        onRemove={onRemove}
        customActions={customActions}
      />
      <Collapse in={isOpen}>
        <Box
          borderBottom={showButtonActions ? '1px' : '0px'}
          borderColor="gray.50"
        >
          <InsurancePolicyRisksTable insurancePolicy={insurancePolicy} />
          {showDocuments && (
            <InsurancePolicyItemCardDocuments
              insurancePolicy={insurancePolicy}
            />
          )}
        </Box>
        {showButtonActions && (
          <VStack align="stretch" p="3">
            {showClauses && (
              <InsurancePolicyClausesButton clauses={insurancePolicy.clauses} />
            )}
            {insurancePolicy.policy_document_link && (
              <InsurancePolicyDocumentButton
                documentUrl={insurancePolicy.policy_document_link}
                partyId={partyId}
              />
            )}
          </VStack>
        )}
      </Collapse>
    </VStack>
  );
};

export default InsurancePolicyItemCard;
